import { OnDestroy, NgZone, Injectable } from '@angular/core';
import { cloneDeep, isEqual } from 'lodash-es';
import { CacheStoragesEnum, IStorageValue } from '../models/cache.models';
import { CacheStorageBase } from './base/cache-storage-base.service';

@Injectable()
export class CacheStorageHybrid extends CacheStorageBase implements OnDestroy {
  private backupIntervalHandle: number;

  private data: { [key: string]: any } = {};
  private isPersistent: boolean;

  private removedItems = [];
  private readonly changedKeys: string[];

  public constructor(
    private readonly cachePrefix: string,
    private readonly ngZone: NgZone,
    private readonly persistentStorage: CacheStorageBase,
    backupFrequency: number = 1000,
    private readonly timeoutValuableDifference = 0.1,
  ) {
    super();

    this.isPersistent = true;
    this.changedKeys = [];
    this.data = this.load();

    this.ngZone.runOutsideAngular(() => {
      this.backupIntervalHandle = setInterval(() => {
        this.save();
      }, backupFrequency) as any as number;
    });
  }

  public getItem<TItem>(key: string, force?: boolean): TItem {
    return this.data[key] ? this.data[key] : null;
  }

  public setItem<TItem>(key: string, value: TItem): number | false {
    const existing = this.data[key];
    if (!existing && !value) {
      return 1;
    }

    if (isEqual(existing, value)) {
      return 1;
    }

    if (value && existing && value.hasOwnProperty('options')) {
      const storageValue: IStorageValue<any> = value as any as IStorageValue<any>;
      if (storageValue.options.hasOwnProperty('cacheExpires') && storageValue.options.hasOwnProperty('preloadExpires')) {
        const time = new Date().getTime();

        const existingStorageValue: IStorageValue<any> = existing as any as IStorageValue<any>;
        if (
          // Value is the same and timeout has changed less than timeoutValuableDifference (10% by default).
          isEqual(existingStorageValue.value, storageValue.value) &&
          CacheStorageHybrid.getRelativeExpirationDifference(
            existingStorageValue.options.cacheExpires,
            storageValue.options.cacheExpires,
            time,
          ) < this.timeoutValuableDifference &&
          CacheStorageHybrid.getRelativeExpirationDifference(
            existingStorageValue.options.preloadExpires,
            storageValue.options.preloadExpires,
            time,
          ) < this.timeoutValuableDifference
        ) {
          return 1;
        }
      }
    }

    this.changedKeys.push(key);
    this.data[key] = value;
    return 1;
  }

  public removeItem(key: string) {
    delete this.data[key];
    this.removedItems.push(key);
  }

  public clear(prefix) {
    super.clear(prefix);
    this.data = {};
  }

  public type() {
    return CacheStoragesEnum.HYBRID;
  }

  public isEnabled(check?: boolean) {
    return this.persistentStorage.isEnabled(check);
  }

  public length() {
    return Object.keys(this.data).length;
  }

  public key(index: number) {
    const keys = Object.keys(this.data);
    return keys.length > index ? keys[index] : null;
  }

  private save() {
    if (!this.isPersistent) {
      return;
    }
    const data = cloneDeep(this.data);
    this.cleanUpPersistentStorage();
    if (this.changedKeys.length > 0) {
      for (const key of this.changedKeys) {
        this.persistentStorage.setItem(key, data[key]);
      }
      this.changedKeys.length = 0;
    }
  }

  private cleanUpPersistentStorage() {
    const removedItems = cloneDeep(this.removedItems);
    this.removedItems = [];
    for (const ri of removedItems) {
      this.persistentStorage.removeItem(ri);
    }
  }

  private load(): { [key: string]: any } {
    const data: { [key: string]: any } = {};
    // Snapshot keys first: getItem may drop an unreadable entry, which would shift the indexes.
    const keys = Array.from({ length: this.persistentStorage.length() }, (_, i) => this.persistentStorage.key(i));
    for (const key of keys) {
      if (key?.startsWith(this.cachePrefix)) {
        data[key] = this.persistentStorage.getItem(key);
      }
    }
    return data;
  }

  public persist(): void {
    if (this.isPersistent) {
      return;
    }
    this.isPersistent = true;
    this.changedKeys.push(...Object.keys(this.data));
    this.save();
  }

  public unPersist(prefix: string): void {
    this.isPersistent = false;
    this.persistentStorage.unPersist(prefix);
  }

  public ngOnDestroy(): void {
    clearInterval(this.backupIntervalHandle);
  }

  private static getRelativeExpirationDifference(oldExpiration: number, newExpiration: number, time: number): number {
    if (newExpiration === oldExpiration) {
      return 0;
    }
    const oldRelativeExpiration = oldExpiration - time;
    const newRelativeExpiration = newExpiration - time;
    return (newRelativeExpiration - oldRelativeExpiration) / newRelativeExpiration;
  }
}
